// src/components/MapScreen.tsx
import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet"; // Import Leaflet itself
import { getHeadersForRequest, numberWithCommas } from "../utils/functions"; // Assuming this exists
import axios from "axios";
import { CustomerList } from "../utils/types"; // Assuming this type exists
// Removed: import { RepeatOneSharp } from "@mui/icons-material";

// Import Chakra UI Components
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Stack,
  Heading,
  VStack,
  Text,
  Spinner, // For loading state
  Alert, // For error state
  AlertIcon,
} from "@chakra-ui/react";
import { CustomerStatus } from "../utils/strings";
import Nav from "../components/navbar";

// --- Configuration for Statuses ---
// Map numeric statuses from your API to meaningful labels and colors
interface StatusConfig {
  id: number;
  description: string;
  color: string;
}

// const STATUS_CONFIG: StatusConfig[] = [
//   { id: 1, label: "Active", color: "#48BB78" }, // Green
//   { id: 2, label: "Inactive", color: "#A0AEC0" }, // Gray (Changed from Red for better contrast maybe)
//   { id: 3, label: "Pending", color: "#ECC94B" }, // Yellow
//   // Add more statuses here if needed
// ];

// Extract just the numeric IDs for initial state/filtering
const ALL_STATUS_IDS = CustomerStatus.map((s) => s.id);
const STATUS_MAP = new Map(CustomerStatus.map((s) => [s.id, s]));
// --- End Status Configuration ---

// --- Utility Function for Colored Markers (Using DivIcon) ---
const createColoredMarkerIcon = (statusId: number | undefined): L.DivIcon => {
  const statusInfo =
    statusId !== undefined ? STATUS_MAP.get(statusId) : undefined;
  const color = statusInfo?.color || "#718096"; // Default Gray color if status unknown or undefined
  const iconHtml = `<div style="background-color: ${color}; width: 1.2rem; height: 1.2rem; display: block; left: -0.6rem; top: -0.6rem; position: relative; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 0 1px rgba(0,0,0,0.5);"></div>`;

  return L.divIcon({
    html: iconHtml,
    className: "custom-div-icon", // Optional: For additional CSS
    iconSize: [0, 0], // Handled by HTML style
    iconAnchor: [0, 0], // Adjust if needed based on HTML visual center
    popupAnchor: [0, -10], // Position popup above the center
  });
};
// --- End Utility Function ---

const createCustomerIcon = (statusId: number | undefined): L.DivIcon => {
  const statusInfo = statusId !== undefined ? STATUS_MAP.get(statusId) : undefined;
  const color = statusInfo?.color || "#718096"; // Default gray

  const iconHtml = `
    <div style="width: 2.5rem; height: 3rem; display: flex; align-items: center; justify-content: center; position: relative;">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="width: 100%; height: 100%;">
        <path fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        <circle cx="12" cy="9" r="2.5" fill="white"/>
      </svg>
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: "custom-div-icon",
    iconSize: [34, 42],       // Approximate size in pixels
    iconAnchor: [12, 32],     // Bottom point of the pin
    popupAnchor: [0, -32],    // Popup appears above the pin
  });
}

// --- Fix default Leaflet Icons (Only needed if NOT using DivIcon) ---
// If you stick with DivIcon, you can potentially remove this block
// import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
// import markerIcon from "leaflet/dist/images/marker-icon.png";
// import markerShadow from "leaflet/dist/images/marker-shadow.png";

// delete (L.Icon.Default.prototype as any)._getIconUrl; // Workaround for potential issues in some setups

// L.Icon.Default.mergeOptions({
//   iconRetinaUrl: markerIcon2x,
//   iconUrl: markerIcon,
//   shadowUrl: markerShadow,
// });
// --- End Icon Fix ---

// Define the CustomerMapScreen component
const CustomerMapScreen = () => {
  const [allCustomers, setAllCustomers] = useState<CustomerList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for selected status filters (using numeric IDs)
  // Initialize with all known status IDs selected
  const [selectedStatuses, setSelectedStatuses] =
    useState<number[]>(ALL_STATUS_IDS);

  useEffect(() => {
    const getAllCustomers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await getHeadersForRequest(); // Ensure this handles potential errors
        const response = await axios.get<CustomerList[]>(
          `${process.env.REACT_APP_TEST_API_URL}/payments/customerBalances`, // Ensure this ENV var is set
          { headers }
        );

        // Basic validation (optional but recommended)
        if (!Array.isArray(response.data)) {
          throw new Error("API did not return an array of customers");
        }

        // No need to sort here unless specifically required for a non-map feature
        // Sorting is computationally more expensive than filtering
        // const sortedFilteredData = response.data.sort(
        //   (a, b) => Number(b.balance) - Number(a.balance)
        // );
        // console.log("Fetched Customers:", response.data);
        setAllCustomers(response.data);
      } catch (err: any) {
        console.error("Error fetching customers:", err);
        setError(err.message || "Failed to fetch customer data.");
      } finally {
        setIsLoading(false);
      }
    };

    getAllCustomers();
  }, []); // Empty dependency array ensures this runs once on mount

  // Type guard to check for valid coordinates
  interface CustomerWithCoords extends CustomerList {
    latitude: number; // Ensure these are numbers after conversion
    longitude: number;
  }

  const hasValidCoordinates = (
    customer: CustomerList
  ): customer is CustomerWithCoords => {
    // Attempt conversion only once
    const lat = Number(customer.latitude);
    const lng = Number(customer.longitude);
    // Check if conversion resulted in a valid number and potentially basic range check
    return (
      !isNaN(lat) &&
      isFinite(lat) &&
      Math.abs(lat) <= 90 &&
      !isNaN(lng) &&
      isFinite(lng) &&
      Math.abs(lng) <= 180
    );
  };

  // Memoize the filtering logic for performance
  const filteredCustomers = useMemo(() => {
    return allCustomers
      .filter(hasValidCoordinates) // First, ensure they have valid coordinates
      .filter(
        (customer) =>
          // ADD THIS CHECK: Ensure status is a number before calling .includes()
          typeof customer.status === "number" &&
          selectedStatuses.includes(customer.status)
      ); // Then, filter by selected status ID
  }, [allCustomers, selectedStatuses]);

  // Handler for checkbox group changes
  const handleStatusChange = (values: (string | number)[]) => {
    // Chakra CheckboxGroup returns string values, convert them back to numbers
    setSelectedStatuses(values.map(Number));
  };

  // Default map center (Arusha, Tanzania)
  const defaultCenter: L.LatLngExpression = [-3.3716, 36.7317];

  return (
    // Use VStack for overall page layout and spacing
    <>
      <Stack position="sticky" top={0} zIndex={1} width="full">
        <Nav pageName={"Customer Locations"} />
      </Stack>

      <VStack
        spacing={4}
        align="stretch"
        p={4}
        height="100vh" // Ensure VStack takes height
      >
        {/* Filter Section */}
        <Box
          borderWidth="1px"
          borderRadius="lg"
          p={4}
          flexShrink={0} /* Prevent shrinking */
        >
          <Heading as="h3" size="md" mb={3}>
            Filter by Status
          </Heading>
          {isLoading ? (
            <Text>Loading filters...</Text>
          ) : error ? (
            <Text color="red.500">Could not load filter options.</Text>
          ) : (
            <CheckboxGroup
              //   colorScheme="blue" // Or your preferred Chakra theme color
              value={selectedStatuses.map(String)} // CheckboxGroup needs string values
              onChange={handleStatusChange}
            >
              <Stack direction={["column", "row"]} spacing={[1, 4]} wrap="wrap">
                {CustomerStatus.map((statusInfo) => (
                  <Checkbox
                    colorScheme={statusInfo.color}
                    key={statusInfo.id}
                    value={String(statusInfo.id)}
                  >
                    {statusInfo.description}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          )}
        </Box>

        {/* Map Section */}
        <Box
          flexGrow={1}
          /* Allow map to take remaining space */ borderWidth="1px"
          borderRadius="lg"
          overflow="hidden"
          position="relative"
        >
          {isLoading ? (
            <Center h="100%">
              {" "}
              {/* Use Center component for easy centering */}
              <Spinner size="xl" />
            </Center>
          ) : error ? (
            <Center h="100%">
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
            </Center>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={14}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%", color: "invert" }}
              key={JSON.stringify(defaultCenter)}
            >
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Default">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>

                {/* <LayersControl.BaseLayer name="Terrain">
                  <TileLayer
                    attribution="Tiles &copy; Esri &mdash; Source: USGS, Esri, TANA, DeLorme, and NPS"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}"
                  />
                </LayersControl.BaseLayer> */}

                <LayersControl.BaseLayer name="Satellite">
                  <TileLayer
                    attribution="Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                </LayersControl.BaseLayer>

                <LayersControl.BaseLayer name="Dark">
                  <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>

              {/* Markers go here */}
              {filteredCustomers.map((customer) => (
                <Marker
                  key={customer.id}
                  position={[
                    Number(customer.latitude),
                    Number(customer.longitude),
                  ]}
                  icon={createCustomerIcon(customer.status)}
                >
                  <Popup>
                    <Box>
                      <Text fontWeight="bold">{customer.customerName}</Text>
                      <Text>
                        Status:{" "}
                        {typeof customer.status === "number"
                          ? STATUS_MAP.get(customer.status)?.description ||
                            `Unknown (${customer.status})`
                          : "N/A"}
                      </Text>
                      <Text>Balance: {numberWithCommas(customer.balance)}</Text>
                      <Text fontSize="sm">
                        Lat: {Number(customer.latitude).toFixed(4)}, Lng:{" "}
                        {Number(customer.longitude).toFixed(4)}
                      </Text>
                    </Box>
                  </Popup>
                </Marker>
              ))}

              {filteredCustomers.length === 0 && !isLoading && (
                <Center
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="rgba(255,255,255,0.7)"
                  zIndex={1000}
                >
                  <Text fontWeight="bold">
                    No customers match the current filters.
                  </Text>
                </Center>
              )}
            </MapContainer>
          )}
        </Box>
      </VStack>
    </>
  );
};

// Helper component for centering content within the map area (or use Chakra's Center)
const Center: React.FC<{ children: React.ReactNode; [key: string]: any }> = ({
  children,
  ...props
}) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="100%"
    {...props}
  >
    {children}
  </Box>
);

export default CustomerMapScreen;
